/**
 * Extract SMSWiser kill identifiers from SIEM event details or audit request body.
 */

export type SmswiserKillContext = {
  wiserDropId?: number;
  uniqueId?: string;
};

function asRecord(v: unknown): Record<string, unknown> | null {
  return v != null && typeof v === 'object' && !Array.isArray(v)
    ? (v as Record<string, unknown>)
    : null;
}

function parseDropId(v: unknown): number | undefined {
  if (typeof v === 'number' && Number.isInteger(v) && v >= 1) return v;
  if (typeof v === 'string' && v.trim() !== '') {
    const n = Number(v);
    if (Number.isInteger(n) && n >= 1) return n;
  }
  return undefined;
}

function parseUniqueId(v: unknown): string | undefined {
  if (typeof v !== 'string') return undefined;
  const t = v.trim();
  return t.length > 0 ? t : undefined;
}

function fromBody(body: unknown): SmswiserKillContext {
  const o = asRecord(body);
  if (!o) return {};
  const wiserDropId = parseDropId(o.dropId);
  const uniqueId = parseUniqueId(o.uniqueId);
  return { ...(wiserDropId != null && { wiserDropId }), ...(uniqueId != null && { uniqueId }) };
}

function merge(a: SmswiserKillContext, b: SmswiserKillContext): SmswiserKillContext {
  return {
    ...(a.wiserDropId != null ? { wiserDropId: a.wiserDropId } : {}),
    ...(a.uniqueId != null ? { uniqueId: a.uniqueId } : {}),
    ...(b.wiserDropId != null && a.wiserDropId == null ? { wiserDropId: b.wiserDropId } : {}),
    ...(b.uniqueId != null && a.uniqueId == null ? { uniqueId: b.uniqueId } : {}),
  };
}

/** SIEM HTTP row: action + details.requestBody / responseBody / top-level. */
export function extractSmswiserKillContextFromSiem(details: Record<string, unknown> | null, action: string | null): SmswiserKillContext {
  const isKill =
    typeof action === 'string' && action.includes('/smswiser/drops/kill');
  if (!isKill || !details) return {};

  const fromReq = fromBody(details.requestBody);
  const fromRes = fromBody(details.responseBody);
  const top = fromBody(details);
  return merge(merge(fromReq, fromRes), top);
}

/** Audit log row: action drop.delete + requestBody; optional entityId numeric. */
export function extractSmswiserKillContextFromAudit(
  action: string,
  requestBody: Record<string, unknown> | null | undefined,
  entityId?: string | null,
): SmswiserKillContext {
  if (action !== 'drop.delete') return {};

  const fromBodyCtx = fromBody(requestBody ?? null);
  const fromEntity = parseDropId(entityId);
  return merge(fromBodyCtx, fromEntity != null ? { wiserDropId: fromEntity } : {});
}

export function hasSmswiserKillInvestigationTarget(ctx: SmswiserKillContext): boolean {
  return ctx.wiserDropId != null || ctx.uniqueId != null;
}
